import {
  getCountryFromLocale,
  getLanguageFromLocale,
  getLocaleFromPathname,
  type Locale,
  removeLocaleFromUrl,
} from '@susu/locale-utils'

import { getAffiliateDataFromCookie } from './awin-properties'
import { getCustomerProperties } from './customer-properties/customer-properties'
import { getGoogleAnalyticsData } from './google-analytics-properties'
import { getGoogleAndFacebookAttributesFromCookies } from './google-and-facebook-properties'
import {
  getMarketingClickIds,
  type MarketingClickIds,
} from './marketing-click-ids'
import { getPageType } from './page-type-properties/page-type-properties'
import { getPlatformProperties } from './platform-properties'

export {
  getGA4ClientIdFromCookie,
  getGA4SessionDataFromCookie,
  getGoogleAnalyticsData,
  resolveGA4MeasurementIdForSegmentWriteKey,
  setGA4MeasurementId,
} from './google-analytics-properties'
export type {
  MarketingClickIdParam,
  MarketingClickIds,
} from './marketing-click-ids'
export {
  getMarketingClickIds,
  MARKETING_CLICK_ID_COOKIE_FALLBACK,
  MARKETING_CLICK_ID_PARAMS,
  MARKETING_CLICK_IDS_EXPIRY_DAYS,
  STORAGE_KEY_MARKETING_CLICK_IDS,
} from './marketing-click-ids'
export type { PageType } from './page-type-properties'
export * from './page-type-properties'

/**
 * Get segment common properties
 *
 * Extracts affiliate and marketing attribution data from cookies and URL params,
 * combining data from multiple sources (AWIN, Google, Facebook, Google Analytics,
 * marketing click IDs) for comprehensive analytics tracking.
 */
export const getSegmentCommonProperties = (
  // Fallback when the URL has no valid locale; otherwise derived from the path.
  fallbackLocale?: Locale,
): {
  affiliateMarker: string
  awc: string
  country: string
  fb_fbc: string
  fb_fbp: string
  ga_aw_clid: string
  ga_client_id: string
  ga_session_id: string
  ga_session_number: string
  last_platform: string
  logged_in: string
  language: string
  location_id: string
  page_type: string
  platform: string
  url: string
  user_id: string
} & MarketingClickIds => {
  const platformProperties = getPlatformProperties()
  const affiliateData = getAffiliateDataFromCookie()
  const googleAnalyticsData = getGoogleAnalyticsData()
  const googleFacebookData = getGoogleAndFacebookAttributesFromCookies()
  const locale =
    fallbackLocale ?? getLocaleFromPathname(window.location.pathname)
  // With a fallback there's no locale segment in the path to strip.
  const url = fallbackLocale
    ? window.location.href
    : removeLocaleFromUrl(new URL(window.location.href))
  const language = getLanguageFromLocale(locale)
  const pageType = getPageType()
  const country = getCountryFromLocale(locale).toLowerCase()
  const customerProperties = getCustomerProperties()
  const isLoggedIn = customerProperties.isLoggedIn
  const customerId = customerProperties.customerId
  const marketingClickIds = getMarketingClickIds()

  return {
    affiliateMarker: affiliateData.affiliateMarker,
    awc: affiliateData.awc,
    country: country,
    fb_fbc: googleFacebookData.fbFbc,
    fb_fbp: googleFacebookData.fbFbp,
    ga_aw_clid: googleFacebookData.gaAwClid,
    ga_client_id: googleAnalyticsData.ga_client_id,
    ga_session_id: googleAnalyticsData.ga_session_id,
    ga_session_number: googleAnalyticsData.ga_session_number,
    logged_in: isLoggedIn,
    language,
    location_id: window.location.href,
    page_type: pageType,
    url,
    user_id: customerId,
    ...platformProperties,
    ...marketingClickIds,
  }
}
